import { useEffect, useState, useRef, useContext, useCallback } from "react";
import "./Header.css";
import logo from "../../assets/logo.png";
import lightLogo from "../../assets/lodos-light-theme-logo.png";
import { useLanguage } from "../../LanguageProvider";
import { BsThreeDots } from "react-icons/bs";
import { AiOutlinePlus } from "react-icons/ai";
import { FiMaximize, FiMinimize } from "react-icons/fi";
import { TbBolt, TbLock, TbLogout, TbTie, TbUser, TbShip } from "react-icons/tb";
import Lockscreen from "../LockScreen/LockScreen";
import Tabs from "./components/TabsHolder/TabsHolder";
import Dropdown from "../Dropdown/Dropdown";
import { useNavigate } from "react-router-dom";
import { GeneralContext } from "../../GeneralProvider";
import AddProjectModal from "./components/TabsHolder/Views/AddProjectModal";
import ReceivedInvitationsModal from "../ReceivedInvitationsModal/ReceivedInvitationsModal";
import Profile from "../../pages/ProfilePage/Profile";
import ThemeToggle from "../ThemeToggle/ThemeToggle";
import CustomPlan from "../../pages/CustomPlan/CustomPlan";
import { PiGooglePlayLogoDuotone } from "react-icons/pi";
import { FaAppStore } from "react-icons/fa";
import { broadcastAuthMessage, subscribeAuthMessage } from "../../utils/authChannel";
import OnboardingVideoModal from "../OnboardingVideoModal/OnboardingVideoModal";

export default function Header() {
  const navigate = useNavigate();
  const {
    projects,
    setSubProjects,
    handleSelectProject,
    handleAddNewProject,
    selectedProjectID,
    setSelectedProjectID,
    userId,
    tabs,
    setTabs,
    selectedTab,
    setSelectedTab,
    receivedProjectInvitations,
    receivedInvitations,
    isSubscriptionExpired,
    openedWindows,
    setOpenedWindows,
    handleUpdateOpenedWindows,
    subscriptionType,
    handleTokenUpdate,
    getUserAuthorizations,
    setIsSubscriptionExpired,
    setSubscriptionRenewDate,
    setSubscriptionType,
    showOnboardingModal,
    setShowOnboardingModal,
    isFirstLogin,
  } = useContext(GeneralContext);

  const { getTranslation } = useLanguage();
  const menuRef = useRef();
  const isInitialMount = useRef(true);
  // eslint-disable-next-line
  const [time, setTime] = useState(new Date().toLocaleTimeString());
  const [menuOpen, setMenuOpen] = useState(false);
  const [showLockscreen, setShowLockscreen] = useState(false);
  const [selectedProject, setSelectedProject] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [logout, setLogout] = useState(false);
  const [isAddingProject, setIsAddingProject] = useState(false);
  const [isReceivedInvitationsModalOpen, setIsReceivedInvitationsModalOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hasUsedFullscreen, setHasUsedFullscreen] = useState(() => {
    return localStorage.getItem("fullscreenUsed") === "true";
  });
  const [showOnboardingModalManual, setShowOnboardingModalManual] = useState(false);

  useEffect(() => {
    const unsubscribe = subscribeAuthMessage((data) => {
      if (!data || typeof data !== "object") return;

      if (data.type === "WHO_HAS_SESSION") {
        const token = sessionStorage.getItem("token");
        const userStr = sessionStorage.getItem("user");
        if (token && userStr) {
          const user = JSON.parse(userStr);
          const rememberMe = localStorage.getItem("rememberMe") === "true";
          broadcastAuthMessage({
            type: "I_HAVE_SESSION",
            token,
            user,
            rememberMe,
          });
        }
        return; // diğer case'lere düşmesin
      }

      if (data.type === "LOGIN" && data.token && data.user) {
        // her zaman oturumu sekme bazında yaz
        sessionStorage.setItem("token", data.token);
        sessionStorage.setItem("user", JSON.stringify(data.user));

        // rememberMe bilgisini öncelikle event'ten, yoksa mevcut storage'tan oku
        const rm =
          typeof data.rememberMe !== "undefined"
            ? !!data.rememberMe
            : localStorage.getItem("rememberMe") === "true";

        if (rm) {
          // kalıcı giriş: localStorage'a da yaz
          localStorage.setItem("token", data.token);
          localStorage.setItem("user", JSON.stringify(data.user));
          localStorage.setItem("rememberMe", "true");
        } else {
          // kalıcı olmasın: varsa kalıntıları temizle ve flag'i false tut
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          localStorage.setItem("rememberMe", "false");
        }

        try {
          handleTokenUpdate?.(data.token);
          getUserAuthorizations?.(data.token, data.user.id);

          if (typeof data.user.isExpired !== "undefined") {
            setIsSubscriptionExpired?.(data.user.isExpired);
          }
          if (data.user.subscriptionExpiredDate) {
            setSubscriptionRenewDate?.(data.user.subscriptionExpiredDate);
          }
          if (data.user.subscriptionPackageType) {
            setSubscriptionType?.(data.user.subscriptionPackageType);
          }
        } catch (_) { }
      }

      if (data.type === "LOGOUT") {
        sessionStorage.removeItem("token");
        sessionStorage.removeItem("user");
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        localStorage.removeItem("rememberMe");
        navigate("/");
      }
    });

    return () => unsubscribe();
  }, [
    handleTokenUpdate,
    getUserAuthorizations,
    setIsSubscriptionExpired,
    setSubscriptionRenewDate,
    setSubscriptionType,
  ]);


  const headerFolders = useCallback(() => ([
    {
      key: "profile",
      name: getTranslation("profile"),
      icon: "TbUser",
      library: { TbUser }, // ikon zaten importlu
      module: Profile,
      multiple: false,
      independent: true,
    },
    {
      key: "subscription",
      name: getTranslation("subscription"),
      icon: "TbBolt",
      library: { TbBolt },
      module: CustomPlan,
      multiple: false,
      independent: true,
    },
  ]), [getTranslation]);

  const resolveHeaderFolder = useCallback((keyOrName) => {
    const all = headerFolders();
    // Önce key ile ara
    let f = all.find(f => f.key === keyOrName);
    if (f) return f;
    // Geriye dönük: name ile ara (yenileme/legacy durumları için)
    f = all.find(f => f.name === keyOrName);
    return f;
  }, [headerFolders]);

  const openModal = useCallback(
    (folderKeyOrName, _opts = {}) => {
      const folder = resolveHeaderFolder(folderKeyOrName);
      if (!folder) return;

      const {
        key,
        name: displayName,
        module,
        icon,
        multiple = false,
        independent = true, // Header modülleri bağımsız pencereler
      } = folder;

      // Mevcut pencere var mı? (key veya geriye dönük name + aktif proje koşulu)
      const existingWindow = openedWindows.find((win) =>
        (win.key === key || win.name === displayName) &&
        !multiple &&
        (independent || win.projectId === selectedProjectID)
      );
      if (existingWindow) return;

      const id = "_" + Math.random().toString(36).substr(2, 9);
      const offset = 20;
      const positionIndex = openedWindows.length;
      const newPosition = {
        x: window.innerWidth / 2 - 650 + offset * positionIndex,
        y: window.innerHeight / 2 - 350 + offset * positionIndex,
      };
      const defaultSize = { width: 1300, height: 750 };

      const newWindowData = {
        key,                    // 🔑 sabit kimlik
        name: displayName,      // 🏷️ görünen ad
        icon,
        position: newPosition,
        size: defaultSize,
        id,
        isActive: true,
        isSelected: true,
        projectId: independent ? "" : (selectedProjectID || ""),
        independent,
        module,                 // Modules.jsx bunu key ile override edebilir
      };

      const updatedWindows = [
        ...openedWindows.map((win) => ({ ...win, isSelected: false })),
        newWindowData,
      ];
      setOpenedWindows(updatedWindows);
      handleUpdateOpenedWindows(updatedWindows);
    },
    [openedWindows, selectedProjectID, setOpenedWindows, handleUpdateOpenedWindows, resolveHeaderFolder]
  );

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "F11") {
        e.preventDefault();
        toggleFullscreen();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const toggleFullscreen = () => {
    if (!hasUsedFullscreen) {
      localStorage.setItem("fullscreenUsed", "true");
      setHasUsedFullscreen(true);
    }
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((err) => console.error("FS enable:", err));
    } else {
      document.exitFullscreen().catch((err) => console.error("FS exit:", err));
    }
  };

  useEffect(() => {
    if (userId === "" || userId === undefined || userId === null) {
      setTabs([]);
      setSelectedProject("");
      setSelectedTab(0);
    }
  }, [userId]);

  useEffect(() => {
    if (!isAddingProject && selectedProjectID) {
      const preselectedProject = projects.find((project) => project.projectID === selectedProjectID);
      if (preselectedProject) {
        setSelectedProject(selectedProjectID);
        if (!tabs.some((tab) => tab.projectID === preselectedProject.projectID)) {
          setTabs((prevTabs) => [
            ...prevTabs,
            { projectTitle: preselectedProject.projectTitle, projectID: preselectedProject.projectID },
          ]);
          setSelectedTab((prevTabs) => prevTabs?.length);
        } else {
          setSelectedTab(tabs.findIndex((tab) => tab.projectID === preselectedProject.projectID));
        }
      }
    }
  }, [selectedProjectID, projects]);

  useEffect(() => {
    if (tabs?.length > 0 && selectedTab && !isAddingProject) {
      const selectedTabObj = tabs.find((tab) => tab.projectID === selectedTab);
      if (!selectedTabObj) return;
      const selectedProject = projects.find((project) => project.projectID === selectedTabObj.projectID);
      if (selectedProject && !logout && selectedProjectID !== selectedProject.projectID) {
        handleSelectProject(selectedProject.projectID, false, false, selectedTabObj.projectTitle);
        const formattedSubProjects =
          selectedProject.subProjects?.map((sub) => ({
            value: sub.subProjectId,
            label: sub.title,
          })) || [];
        setSubProjects(formattedSubProjects);
        setSelectedProject(selectedProject.projectID);
      }
    }
  }, [tabs, selectedTab, projects, logout, selectedProjectID, isAddingProject]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    localStorage.setItem("currentTabs", JSON.stringify(tabs));
    localStorage.setItem("selectedTabIndex", selectedTab);
  }, [tabs, selectedTab]);

  useEffect(() => {
    let renamedTabIndex = localStorage.getItem("selectedTabIndex");
    closeTab(renamedTabIndex);
  }, [projects]);

  useEffect(() => {
    const storedTabs = JSON.parse(localStorage.getItem("currentTabs"));
    const storedIndex = parseInt(localStorage.getItem("selectedTabIndex"), 10);

    if (Array.isArray(storedTabs) && storedTabs.length > 0) {
      setTabs(storedTabs);
      if (storedIndex >= 0 && storedIndex < storedTabs.length) {
        setSelectedTab(storedIndex);
        const { projectID, projectTitle } = storedTabs[storedIndex];
        const project = projects.find((p) => p.projectID === projectID);
        if (project) {
          handleSelectProject(projectID, false, false, projectTitle);
          const formattedSubProjects =
            project.subProjects?.map((sub) => ({
              value: sub.subProjectId,
              label: sub.title,
            })) || [];
          setSubProjects(formattedSubProjects);
          setSelectedProject(projectID);
        }
      }
    }
  }, [projects]);

  useEffect(() => {
    if (projects?.length === 1 && !logout) {
      const singleProject = projects[0];
      setSelectedProject(singleProject.projectID);
      setTabs([{ projectTitle: singleProject.projectTitle, projectID: singleProject.projectID }]);
    }
  }, [projects]);

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  const closeTab = (index) => {
    setTabs((prevTabs) => {
      const updatedTabs = prevTabs.filter((_, i) => i !== index);

      let newIndex;
      if (index === selectedTab) {
        newIndex = index > 0 ? index - 1 : 0;
      } else if (index < selectedTab) {
        newIndex = selectedTab - 1;
      } else {
        newIndex = selectedTab;
      }

      if (updatedTabs.length > 0) {
        setSelectedTab(newIndex);
        const newTab = updatedTabs[newIndex];
        if (newTab) {
          handleSelectProject(newTab.projectID, false, false, newTab.projectTitle);
          const formattedSubProjects =
            projects.find((p) => p.projectID === newTab.projectID)?.subProjects?.map((sub) => ({
              value: sub.subProjectId,
              label: sub.title,
            })) || [];
          setSubProjects(formattedSubProjects);
          setSelectedProject(newTab.projectID);
        }
      } else {
        setSelectedTab("");
        setSelectedProjectID("");
      }
      return updatedTabs;
    });
  };

  const handleTabClick = (index) => {
    const tabData = tabs[index];
    if (!tabData) return;

    const project = projects.find((p) => p.projectID === tabData.projectID);
    if (!project) return;

    setSelectedTab(index);
    setSelectedProject(project.projectID);

    handleSelectProject(project.projectID, false, false, project.projectTitle);

    const formattedSubProjects =
      project.subProjects?.map((sub) => ({
        value: sub.subProjectId,
        label: sub.title,
      })) || [];
    setSubProjects(formattedSubProjects);
  };

  const handleAddProject = async (title, description) => {
    setIsAddingProject(true);
    try {
      const newProject = await handleAddNewProject(title, description);
      if (newProject) {
        const { projectId, title: createdTitle } = newProject;
        setTabs((prevTabs) => [...prevTabs, { projectTitle: createdTitle, projectID: projectId }]);
        setSelectedTab(tabs.length);
      }
    } catch (error) {
      console.error("Error while adding project:", error);
    }
    setIsAddingProject(false);
  };

  const handleProjectSelect = (projectID) => {
    const selectedProject = projects.find((project) => project.projectID === projectID);
    if (selectedProject) {
      handleSelectProject(projectID, false, false, selectedProject.projectTitle);
      const formattedSubProjects = selectedProject?.subProjects?.map((sub) => ({
        value: sub.subProjectId,
        label: sub.title,
      }));
      setSubProjects(formattedSubProjects);
      setSelectedProject(projectID);

      if (!tabs.some((tab) => tab.projectID === selectedProject.projectID)) {
        setTabs((prevTabs) => [
          ...prevTabs,
          { projectTitle: selectedProject.projectTitle, projectID: selectedProject.projectID },
        ]);
        setSelectedTab((prevTabs) => prevTabs?.length);
      } else {
        setSelectedTab(tabs.findIndex((tab) => tab.projectID === selectedProject.projectID));
      }
    }
  };

  const handleLogout = () => {
    setLogout(true);
    setTabs([]);
    setSelectedProject("");
    setSelectedProjectID("");
    localStorage.clear();
    sessionStorage.clear();
    localStorage.removeItem("username");
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("rememberMe");
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("user");
    sessionStorage.removeItem("temp_username");
    localStorage.removeItem("openedWindows");
    broadcastAuthMessage({ type: "LOGOUT" });
    navigate("/");
  };

  function formatDateToCustomString(date) {
    const months = [
      getTranslation("january").slice(0, 3),
      getTranslation("february").slice(0, 3),
      getTranslation("march").slice(0, 3),
      getTranslation("april").slice(0, 3),
      getTranslation("may").slice(0, 3),
      getTranslation("june").slice(0, 3),
      getTranslation("july").slice(0, 3),
      getTranslation("august").slice(0, 3),
      getTranslation("september").slice(0, 3),
      getTranslation("october").slice(0, 3),
      getTranslation("november").slice(0, 3),
      getTranslation("december").slice(0, 3),
    ];
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  }

  function formatTimeToCustomString(date) {
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  }

  const date = new Date();
  const formattedDate = formatDateToCustomString(date);
  const formattedTime = formatTimeToCustomString(date);

  useEffect(() => {
    const timer = setInterval(() => {
      setTime(new Date().toLocaleTimeString());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const lockStatus = localStorage.getItem("isLocked");
    setShowLockscreen(lockStatus === "true");
  }, []);

  return (
    <>
      <header>
        {localStorage.getItem("app-theme") === "light" ? (
          <div className="logo-container">
            <div className="logo-inner-container">
              <img src={lightLogo} width={30} alt="logo" />
            </div>
          </div>
        ) : (
          <div className="logo-container">
            <div className="logo-inner-container">
              <img src={logo} width={30} alt="logo" />
            </div>
          </div>
        )}

        {isSubscriptionExpired || subscriptionType === "social-hive" ? null : (
          <>
            {projects && projects?.length >= 1 ? (
              <>
                <Dropdown
                  style={{ marginTop: "2px" }}
                  options={projects?.map((project) => ({
                    value: project.projectID,
                    label: project.projectTitle,
                  }))}
                  placeholder={getTranslation("workSpace")}
                  onChange={(e) => handleProjectSelect(e.target.value)}
                  value={selectedProjectID}
                  addWorkspaceOption={{
                    label: getTranslation("createNewWorkspace"),
                    onClick: () => {
                      handleOpenModal();
                    },
                  }}
                />
                <Tabs
                  tabs={[...new Map(tabs.map((tab) => [tab.projectID, tab])).values()]}
                  selectedTab={selectedTab}
                  handleTabClick={handleTabClick}
                  closeTab={closeTab}
                  onAddNewTab={(newTab) => {
                    setTabs((prevTabs) => [...prevTabs, newTab]);
                    setSelectedTab(tabs.length);
                  }}
                />
              </>
            ) : (
              <div className="custom-select-container">
                <button className="custom-create-button" onClick={handleOpenModal}>
                  {getTranslation("createNewWorkspace")}
                  <AiOutlinePlus className="icon" />
                </button>
              </div>
            )}
          </>
        )}

        <div className="actions">
          <ThemeToggle />
          <div className="date-time-container">
            <div className="time-display">{formattedTime}</div>
            <div className="date-display">{formattedDate}</div>
          </div>
          <div className="profile-container">
            <div onClick={toggleFullscreen} style={{ display: "inline-block", position: "relative" }}>
              {isFullscreen ? (
                <FiMinimize size={20} style={{ marginTop: "4px" }} />
              ) : (
                <FiMaximize size={20} style={{ marginTop: "4px" }} />
              )}
              {!hasUsedFullscreen && <span className="blinking-light"></span>}
            </div>

            {userId && (
              <div 
                onClick={() => setShowOnboardingModalManual(true)} 
                style={{ display: "inline-block", cursor: "pointer" }}
                title={getTranslation("watchOnboardingVideo") || "Watch Onboarding Video"}
              >
                <TbShip size={20} style={{ marginTop: "4px" }} />
              </div>
            )}

            {isSubscriptionExpired ? (
              <></>
            ) : (
              <div
                className="icon-wrapper"
                onClick={() => {
                  setIsReceivedInvitationsModalOpen(true);
                }}
              >
                <TbTie size={20} style={{ marginTop: "4px" }} />
                {receivedInvitations.length + receivedProjectInvitations.length > 0 && (
                  <span className="blinking-light" />
                )}
              </div>
            )}

            <TbLock
              size={20}
              onClick={() => {
                setShowLockscreen(true);
                localStorage.setItem("isLocked", "true");
              }}
            />

            <div className="menu-container" ref={menuRef}>
              <BsThreeDots size={20} onClick={() => setMenuOpen(!menuOpen)} />
              {menuOpen && (
                <div className="dropdown-menu top" style={{ zIndex: 99999 }}>
                  <div
                    onClick={() => {
                      setMenuOpen(false);
                      openModal("profile");
                    }}
                    className="menu-item"
                  >
                    <TbUser size={18} style={{ minWidth: "20px" }} />
                    <span>{getTranslation("profile")}</span>
                  </div>

                  <div
                    onClick={() => {
                      setMenuOpen(false);
                      openModal("subscription");
                    }}
                    className="menu-item"
                  >
                    <TbBolt size={18} style={{ minWidth: "20px" }} />
                    <span>{getTranslation("subscription")}</span>
                  </div>

                  <a
                    href="https://play.google.com/store/apps/details?id=io.lodos.app&pli=1"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="menu-item"
                    style={{ textDecoration: "none" }}
                  >
                    <PiGooglePlayLogoDuotone size={18} style={{ minWidth: "20px" }} />
                    <span>Android App</span>
                  </a>

                  <a
                    href="https://apps.apple.com/tr/app/lodos-io/id6745250622"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="menu-item"
                    style={{ textDecoration: "none" }}
                  >
                    <FaAppStore size={18} style={{ minWidth: "20px" }} />
                    <span>iOS App</span>
                  </a>

                  <div onClick={handleLogout} className="menu-item">
                    <TbLogout size={18} style={{ minWidth: "20px" }} />
                    <span>{getTranslation("logout")}</span>
                  </div>
                  <span
                    style={{
                      width: "100%",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      fontSize: "12px",
                      display: "flex",
                      marginTop: "5px",
                    }}
                  >
                    v{process.env.REACT_APP_VERSION}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <AddProjectModal isOpen={isModalOpen} onClose={handleCloseModal} onCreate={handleAddProject} isWorkspace={true} />

      <ReceivedInvitationsModal
        isOpen={isReceivedInvitationsModalOpen}
        onClose={() => setIsReceivedInvitationsModalOpen(false)}
      />

      {showLockscreen && <Lockscreen onClose={() => setShowLockscreen(false)} />}
      
      <OnboardingVideoModal 
        isOpen={showOnboardingModalManual} 
        onClose={() => setShowOnboardingModalManual(false)}
        isFirstLogin={false}
      />
    </>
  );
}